import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, MessageCircle, Gauge, Radar, Camera, ScanText,
  ClipboardCheck, EyeOff, ShieldCheck, Zap, UserCheck, BadgeCheck,
  Building2, Route, Landmark, Factory, Briefcase, CircleDot, Monitor, FileText, Scale,
  Clock, AlertTriangle, Eye,
} from "lucide-react";
import { Reveal } from "../../components/Reveal";
import { WA_NUMBER } from "../../data/site";

const HIGHLIGHTS = ["Real-Time Speed Monitoring", "Automatic Violation Detection", "Vehicle & License Plate Capture", "Automated Violation Logging"];

const CHALLENGES = [
  { icon: EyeOff, title: "Limited Monitoring Resources", desc: "Manual monitoring cannot continuously cover all roads and traffic locations." },
  { icon: Clock, title: "Delayed Violation Processing", desc: "Manual verification can slow down the identification and processing of violations." },
  { icon: AlertTriangle, title: "Road Safety Risks", desc: "Undetected speeding violations can contribute to unsafe driving conditions and accidents." },
];

const WORKFLOW = [
  { icon: Monitor, step: "Monitor", desc: "The system continuously monitors vehicles and measures their speed." },
  { icon: Radar, step: "Detect", desc: "AI analytics identify vehicles exceeding the configured speed limit." },
  { icon: Camera, step: "Capture", desc: "The system captures images of the violating vehicle and its license plate." },
  { icon: FileText, step: "Log", desc: "Violation details are automatically recorded for verification and enforcement." },
  { icon: Scale, step: "Enforce", desc: "The recorded violation can be used to support automated penalty generation and faster enforcement." },
];

const CAPABILITIES = [
  { icon: Gauge, title: "Real-Time Speed Monitoring", desc: "Continuously monitors vehicle movement and speed." },
  { icon: Radar, title: "Automatic Violation Detection", desc: "Identifies vehicles exceeding configured speed limits." },
  { icon: Camera, title: "Vehicle Image Capture", desc: "Captures images of vehicles involved in speed violations." },
  { icon: ScanText, title: "License Plate Capture", desc: "Records the license plate of the violating vehicle." },
  { icon: ClipboardCheck, title: "Automated Violation Logging", desc: "Logs detected violations automatically for further processing." },
  { icon: Eye, title: "Reduced Manual Monitoring", desc: "Helps reduce the need for continuous manual observation." },
];

const IMPACT = [
  { icon: ShieldCheck, title: "Improved Road Safety", desc: "Helps deter speeding and supports safer driving behaviour." },
  { icon: Zap, title: "Faster Enforcement", desc: "Automated detection and violation logging can reduce delays in processing violations." },
  { icon: UserCheck, title: "Reduced Manual Effort", desc: "Automates continuous speed monitoring and violation identification." },
  { icon: BadgeCheck, title: "Improved Compliance", desc: "Supports more consistent enforcement of configured speed limits." },
];

const DEPLOYMENTS = [
  { icon: Building2, label: "Urban Roads" },
  { icon: Route, label: "Highways" },
  { icon: Landmark, label: "Smart City Infrastructure" },
  { icon: Factory, label: "Industrial Campuses" },
  { icon: Briefcase, label: "Corporate Campuses" },
  { icon: CircleDot, label: "Controlled Traffic Zones" },
];

const BlockTitle = ({ kicker, children }) => (
  <Reveal>
    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">{kicker}</p>
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-10">{children}</h2>
  </Reveal>
);

const HeroVisual = () => (
  <div className="relative rounded-3xl overflow-hidden border border-white/12 shadow-2xl shadow-black/50">
    <img src="/products/speed-detection-1.png" alt="AI speed detection with live speed overlays" className="w-full object-cover" />
    <motion.div
      initial={{ top: "-10%" }}
      animate={{ top: "110%" }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
      className="absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-smipl-orange to-transparent opacity-80"
    />
    <div className="absolute top-4 left-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30 flex items-center gap-2">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-smipl-orange" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-smipl-orange" />
      </span>
      Live Detection
    </div>
  </div>
);

const SpeedDetectionPage = ({ openContact }) => (
  <main className="pt-36 pb-28 min-h-screen" data-testid="product-page-vehicle-speed-violation">
    <div className="max-w-6xl mx-auto px-6">
      <Reveal>
        <Link to="/products" data-testid="speed-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors">
          <ArrowLeft size={13} /> Our Products <span className="text-white/20">/</span>
          <span className="text-slate-400 normal-case tracking-normal">Vehicle Speed Detection</span>
        </Link>
      </Reveal>

      <div className="mt-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal delay={0.05}>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">Video Analytics & Surveillance AI</p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
              Vehicle Speed Detection and Violation Enforcement
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
              An AI-powered traffic monitoring solution that continuously detects vehicle speeds, identifies speed
              violations, captures vehicle and license plate images, and automatically logs violations for faster enforcement.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {HIGHLIGHTS.map((h) => (
                <span key={h} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-medium text-slate-200">
                  {h}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="speed-talk-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
                Discuss Your Requirement <ArrowUpRight size={15} />
              </button>
              <Link to="/products" data-testid="speed-back-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-smipl-orange hover:text-smipl-orange transition-all duration-300">
                <ArrowLeft size={15} /> Back to Products
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <HeroVisual />
        </Reveal>
      </div>

      <section className="mt-28" data-testid="speed-challenge">
        <BlockTitle kicker="The Problem">The Challenge</BlockTitle>
        <Reveal delay={0.05}>
          <p className="max-w-3xl text-slate-400 text-base leading-relaxed mb-10">
            Enforcing speed limits across urban roads and highways can be challenging when traffic authorities have
            limited resources for continuous manual monitoring. Many violations may go undetected, while manual
            verification and penalty processes can be time-consuming and inefficient — leading to delayed enforcement,
            reduced compliance with speed limits, and increased road safety risks.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-5">
          {CHALLENGES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="tracing-border h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 border border-red-500/25 text-red-400">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="speed-solution">
        <div className="rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/[0.08] to-transparent p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <BlockTitle kicker="The Solution">AI-Powered Speed Detection for Automated Enforcement</BlockTitle>
            <Reveal delay={0.08}>
              <div className="space-y-4 text-slate-300 text-base leading-relaxed -mt-4">
                <p>
                  The Vehicle Speed Detection and Violation Enforcement system continuously monitors vehicle speeds
                  using AI-powered video analytics.
                </p>
                <p>
                  When a vehicle exceeds the configured speed limit, the system automatically detects the violation and
                  captures images of the vehicle along with its license plate. The violation is automatically logged,
                  enabling a faster and more efficient enforcement process.
                </p>
                <p>
                  The solution reduces dependency on continuous manual monitoring while helping authorities improve
                  compliance with speed regulations.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative rounded-2xl overflow-hidden border border-white/12">
              <img src="/products/speed-detection-2.png" alt="Multi-vehicle AI speed tracking on a highway" className="w-full object-cover" />
              <div className="absolute bottom-4 right-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30">
                Multi-Vehicle Tracking
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mt-28" data-testid="speed-workflow">
        <BlockTitle kicker="Workflow">How the System Works</BlockTitle>
        <div className="relative">
          <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-white/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-smipl-orange origin-left"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {WORKFLOW.map((w, i) => (
              <Reveal key={w.step} delay={i * 0.1}>
                <div data-testid={`speed-step-${i + 1}`} className="relative rounded-2xl border border-white/8 bg-navy-deep/60 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-smipl-orange text-navy-deep font-mono text-xs font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <w.icon size={20} className="mt-5 text-smipl-orange" />
                  <h3 className="mt-3 font-display text-base font-bold">{w.step}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-28" data-testid="speed-capabilities">
        <BlockTitle kicker="Capabilities">Key Capabilities</BlockTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.07}>
              <div className="tracing-border group h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange group-hover:bg-smipl-orange group-hover:text-white transition-all duration-300">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-base font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="speed-impact">
        <BlockTitle kicker="Business Impact">Driving Safer and More Efficient Traffic Enforcement</BlockTitle>
        <div className="grid sm:grid-cols-2 gap-5">
          {IMPACT.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <c.icon size={26} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-xl font-bold">{c.title}</h3>
                <p className="mt-2 text-sm md:text-base text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="speed-deployments">
        <BlockTitle kicker="Deployments">Where It Can Be Deployed</BlockTitle>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {DEPLOYMENTS.map((d, i) => (
            <Reveal key={d.label} delay={(i % 3) * 0.06}>
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-5 transition-all duration-300 hover:border-smipl-orange/40 hover:-translate-y-0.5">
                <d.icon size={18} className="shrink-0 text-smipl-orange" />
                <span className="text-sm font-medium text-slate-200">{d.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Reveal delay={0.1}>
        <div className="mt-28 rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/10 to-transparent p-10 md:p-14 text-center" data-testid="speed-cta">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Make Traffic Monitoring <span className="text-smipl-orange">Smarter and More Efficient</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-slate-400 text-base leading-relaxed">
            Explore how AI-powered vehicle speed detection can help automate monitoring, identify violations,
            and support faster enforcement.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <button data-testid="speed-experts-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-8 py-4 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
              Talk to Our Experts <ArrowUpRight size={15} />
            </button>
            <button
              data-testid="speed-whatsapp-btn"
              onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
              className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-8 py-4 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
            >
              <MessageCircle size={15} /> WhatsApp
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  </main>
);

export default SpeedDetectionPage;
